import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Box, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, CircularProgress, Alert, Snackbar, Chip, MenuItem, Select, FormControl, InputLabel, Autocomplete, Tabs, Tab, Divider } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { getEnglishInterviewIds, addEnglishInterviewId, deleteEnglishInterviewId, getInterviewQuestion, getCompanyCredits, updateCompanyCredits } from "../../services/api.service";
import "./settings.component.scss";

interface JobQuestionOption {
    id: string;
    name: string;
}

const SettingsComponent = () => {
    const { company_id } = useParams<{ company_id: string }>();
    const [activeTab, setActiveTab] = useState<"bypass-language" | "credits">("credits");
    const [jobQuestionData, setJobQuestionData] = useState<{ en: string[], th: string[] }>({ en: [], th: [] });
    const [availableJobQuestions, setAvailableJobQuestions] = useState<JobQuestionOption[]>([]);
    const [selectedJobQuestion, setSelectedJobQuestion] = useState<JobQuestionOption | null>(null);
    const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'th'>('en');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingCredits, setIsLoadingCredits] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" as "success" | "error" | "warning" | "info" });

    const [creditUsage, setCreditUsage] = useState<{
        report_credit_total: number | null;
        report_credit_used: number;
        template_question_credit_total: number | null;
        template_question_credit_used: number;
        video_credit_total_minutes?: number | null;
        video_credit_used_minutes?: number;
        video_credit_remaining_minutes?: number | null;
    } | null>(null);
    const [reportCreditInput, setReportCreditInput] = useState<string>("");
    const [templateCreditInput, setTemplateCreditInput] = useState<string>("");
    const [videoCreditInput, setVideoCreditInput] = useState<string>("");
    const [creditInputErrors, setCreditInputErrors] = useState<{ report?: string; template?: string; video?: string }>({});

    const fetchJobQuestionList = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getEnglishInterviewIds(company_id);
            if (response.data.is_success && response.data.data) {
                setJobQuestionData(response.data.data);
            } else {
                setJobQuestionData({ en: [], th: [] });
                showSnackbar("Failed to fetch job question IDs", "error");
            }
        } catch (error) {
            console.error("Error fetching job question IDs:", error);
            showSnackbar("Error fetching job question IDs", "error");
            setJobQuestionData({ en: [], th: [] });
        } finally {
            setIsLoading(false);
        }
    }, [company_id]);

    const fetchAvailableJobQuestions = useCallback(async () => {
        try {
            // Fetch all job questions for the company with a large limit
            const response = await getInterviewQuestion(1, 1000);
            if (response.data.is_success && response.data.data) {
                const questions = response.data.data.map((item: any) => ({
                    id: item.job_question_id,
                    name: item.job_position?.title || item.job_question_id || 'Untitled'
                }));
                setAvailableJobQuestions(questions);
            }
        } catch (error) {
            console.error("Error fetching available job questions:", error);
        }
    }, []);

    const fetchCompanyCredits = useCallback(async () => {
        if (!company_id) return;
        setIsLoadingCredits(true);
        try {
            const res = await getCompanyCredits(company_id);
            if (res.data?.is_success && res.data?.data) {
                const d = res.data.data;
                setCreditUsage(d);
                setReportCreditInput(d.report_credit_total === null ? "" : String(d.report_credit_total));
                setTemplateCreditInput(d.template_question_credit_total === null ? "" : String(d.template_question_credit_total));
                setVideoCreditInput(
                    d.video_credit_total_minutes === null || d.video_credit_total_minutes === undefined
                        ? ""
                        : String(d.video_credit_total_minutes)
                );
            } else {
                showSnackbar("Failed to fetch company credits", "error");
            }
        } catch (error) {
            console.error("Error fetching company credits:", error);
            showSnackbar("Error fetching company credits", "error");
        } finally {
            setIsLoadingCredits(false);
        }
    }, [company_id]);

    useEffect(() => {
        if (company_id) {
            fetchJobQuestionList();
            fetchAvailableJobQuestions();
            fetchCompanyCredits();
        }
    }, [company_id, fetchJobQuestionList, fetchAvailableJobQuestions, fetchCompanyCredits]);

    const showSnackbar = (message: string, severity: "success" | "error" | "warning" | "info") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleDelete = async (job_question_id: string, language: 'en' | 'th') => {
        if (!window.confirm(`Are you sure you want to delete job question ID: ${job_question_id} from ${language.toUpperCase()}?`)) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await deleteEnglishInterviewId(company_id, job_question_id, language);
            if (response.data.is_success) {
                const updatedData = { ...jobQuestionData };
                updatedData[language] = updatedData[language].filter(id => id !== job_question_id);
                setJobQuestionData(updatedData);
                showSnackbar("Job question ID deleted successfully", "success");
            } else {
                showSnackbar("Failed to delete job question ID", "error");
            }
        } catch (error) {
            console.error("Error deleting job question ID:", error);
            showSnackbar("Error deleting job question ID", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleAdd = async () => {
        if (!selectedJobQuestion || !selectedJobQuestion.id) {
            showSnackbar("Please select a job question", "warning");
            return;
        }

        setIsLoading(true);
        try {
            const response = await addEnglishInterviewId(company_id, selectedJobQuestion.id, selectedLanguage);
            if (response.data.is_success) {
                const updatedData = { ...jobQuestionData };
                updatedData[selectedLanguage] = [...updatedData[selectedLanguage], selectedJobQuestion.id];
                setJobQuestionData(updatedData);
                setSelectedJobQuestion(null);
                showSnackbar("Job question ID added successfully", "success");
            } else {
                showSnackbar(response.data.error || "Failed to add job question ID", "error");
            }
        } catch (error) {
            console.error("Error adding job question ID:", error);
            showSnackbar("Error adding job question ID", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveCredits = async () => {
        if (!company_id) return;
        if (!creditUsage) {
            showSnackbar("Credit usage is not loaded yet", "warning");
            return;
        }
        const toNumberOrNull = (s: string): number | null => {
            const t = String(s || "").trim();
            if (!t) return null; // unlimited
            const n = Math.floor(Number(t));
            if (!Number.isFinite(n) || n < 0) return 0;
            return n;
        };

        setIsLoadingCredits(true);
        try {
            const nextReportTotal = toNumberOrNull(reportCreditInput);
            const nextTemplateTotal = toNumberOrNull(templateCreditInput);
            const nextVideoTotal = toNumberOrNull(videoCreditInput);

            const nextErrors: { report?: string; template?: string; video?: string } = {};
            if (typeof nextReportTotal === "number" && nextReportTotal < creditUsage.report_credit_used) {
                nextErrors.report = `ต้องไม่น้อยกว่าที่ใช้ไปแล้ว (${creditUsage.report_credit_used})`;
            }
            if (typeof nextTemplateTotal === "number" && nextTemplateTotal < creditUsage.template_question_credit_used) {
                nextErrors.template = `ต้องไม่น้อยกว่าที่ใช้ไปแล้ว (${creditUsage.template_question_credit_used})`;
            }
            const videoUsed = Number(creditUsage.video_credit_used_minutes || 0);
            if (typeof nextVideoTotal === "number" && nextVideoTotal < videoUsed) {
                nextErrors.video = `ต้องไม่น้อยกว่าที่ใช้ไปแล้ว (${videoUsed} นาที)`;
            }
            setCreditInputErrors(nextErrors);
            if (nextErrors.report || nextErrors.template || nextErrors.video) {
                showSnackbar("กรุณาตรวจสอบค่าเครดิต (ห้ามน้อยกว่า used ปัจจุบัน)", "warning");
                setIsLoadingCredits(false);
                return;
            }

            const body = {
                report_credit_total: nextReportTotal,
                template_question_credit_total: nextTemplateTotal,
                video_credit_total_minutes: nextVideoTotal,
            };
            const res = await updateCompanyCredits(company_id, body);
            if (res.data?.is_success) {
                showSnackbar("Credits updated successfully", "success");
                await fetchCompanyCredits();
            } else {
                showSnackbar(res.data?.error || "Failed to update credits", "error");
            }
        } catch (error) {
            console.error("Error updating credits:", error);
            showSnackbar("Error updating credits", "error");
        } finally {
            setIsLoadingCredits(false);
        }
    };

    return (
        <Box sx={{ width: '100%' }} className="settings-container">
            <div className="settings-tabs">
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {/* By Pass Language tab is hidden for now (keep code for future use) */}
                    {/* <Tab value="bypass-language" label="By Pass Language" /> */}
                    <Tab value="credits" label="Credits" />
                </Tabs>
                <Divider sx={{ marginBottom: "16px" }} />
            </div>

            {activeTab === "credits" && (
                <div className="credits-settings">
                    <h3>Company Credits</h3>

                    <Alert severity="info" sx={{ marginBottom: '20px' }}>
                        ตั้งค่าเครดิตระดับบริษัท (shared pool) สำหรับ (1) Report Credit และ (2) Template Question Credit
                        <div style={{ marginTop: 8, lineHeight: 1.5 }}>
                            <div>
                                <b>Report Credit</b>: ใช้เมื่อ <b>รายงาน (interview_report)</b> เปลี่ยนสถานะเป็น <b>COMPLETED</b> (นับเป็น used 1 ต่อ 1 รายงาน) และ <b>ไม่คืนเครดิต</b>.
                            </div>
                            <div style={{ marginTop: 6 }}>
                                <b>Template Question Credit</b>: ใช้สำหรับจำกัดจำนวน <b>ชุดคำถามที่ ACTIVE</b> ของบริษัท (ถ้าเกินจะไม่สามารถสร้างชุดคำถามใหม่ได้).
                            </div>
                            <div style={{ marginTop: 6 }}>
                                <b>Unlimited</b>: เว้นว่าง = ไม่จำกัด.
                            </div>
                        </div>
                    </Alert>

                    {isLoadingCredits && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                            <CircularProgress />
                        </div>
                    )}

                    {!isLoadingCredits && creditUsage && (
                        <>
                            <div className="credits-summary">
                                <Chip
                                    label={`Report Credit Used: ${creditUsage.report_credit_used} / ${creditUsage.report_credit_total ?? "Unlimited"}`}
                                    color="primary"
                                />
                                <Chip
                                    label={`Template Credit Used: ${creditUsage.template_question_credit_used} / ${creditUsage.template_question_credit_total ?? "Unlimited"}`}
                                    color="secondary"
                                />
                                <Chip
                                    label={`Video Credit Used: ${creditUsage.video_credit_used_minutes ?? 0} min / ${creditUsage.video_credit_total_minutes ?? "Unlimited"} min`}
                                    color="default"
                                />
                            </div>

                            <div className="credits-form">
                                <TextField
                                    label="Report Credit (total)"
                                    value={reportCreditInput}
                                    onChange={(e) => {
                                        setReportCreditInput(e.target.value);
                                        setCreditInputErrors((prev) => ({ ...prev, report: undefined }));
                                    }}
                                    type="number"
                                    placeholder="เว้นว่าง = Unlimited"
                                    inputProps={{ min: 0, step: 1 }}
                                    fullWidth
                                    error={Boolean(creditInputErrors.report)}
                                    helperText={creditInputErrors.report || (creditUsage ? `Used now: ${creditUsage.report_credit_used}` : "")}
                                />
                                <TextField
                                    label="Template Question Credit (total)"
                                    value={templateCreditInput}
                                    onChange={(e) => {
                                        setTemplateCreditInput(e.target.value);
                                        setCreditInputErrors((prev) => ({ ...prev, template: undefined }));
                                    }}
                                    type="number"
                                    placeholder="เว้นว่าง = Unlimited"
                                    inputProps={{ min: 0, step: 1 }}
                                    fullWidth
                                    error={Boolean(creditInputErrors.template)}
                                    helperText={creditInputErrors.template || (creditUsage ? `Used now: ${creditUsage.template_question_credit_used}` : "")}
                                />
                                <TextField
                                    label="Video Credit (total minutes)"
                                    value={videoCreditInput}
                                    onChange={(e) => {
                                        setVideoCreditInput(e.target.value);
                                        setCreditInputErrors((prev) => ({ ...prev, video: undefined }));
                                    }}
                                    type="number"
                                    placeholder="เว้นว่าง = Unlimited"
                                    inputProps={{ min: 0, step: 1 }}
                                    fullWidth
                                    error={Boolean(creditInputErrors.video)}
                                    helperText={
                                        creditInputErrors.video ||
                                        (creditUsage ? `Used now: ${creditUsage.video_credit_used_minutes ?? 0} min` : "")
                                    }
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSaveCredits}
                                    disabled={
                                        isLoadingCredits ||
                                        Boolean(creditInputErrors.report) ||
                                        Boolean(creditInputErrors.template) ||
                                        Boolean(creditInputErrors.video)
                                    }
                                >
                                    Save Credits
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}

            {activeTab === "bypass-language" && (
                <div className="by-pass-settings">
                <h3>By Pass Language</h3>
                
                {/* Info message */}
                <Alert severity="info" sx={{ marginBottom: '20px' }}>
                    Note: Each job question ID can only be assigned to one language (either English or Thai).
                </Alert>
                
                {/* Summary chips */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                    <Chip label={`English: ${jobQuestionData.en.length} IDs`} color="primary" />
                    <Chip label={`Thai: ${jobQuestionData.th.length} IDs`} color="secondary" />
                </div>
                
                {/* Add new job question ID */}
                <div className="add-job-question">
                    <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Language</InputLabel>
                        <Select
                            value={selectedLanguage}
                            label="Language"
                            onChange={(e) => setSelectedLanguage(e.target.value as 'en' | 'th')}
                            disabled={isLoading}
                        >
                            <MenuItem value="en">English (EN)</MenuItem>
                            <MenuItem value="th">Thai (TH)</MenuItem>
                        </Select>
                    </FormControl>
                    <Autocomplete
                        value={selectedJobQuestion}
                        onChange={(event, newValue) => {
                            setSelectedJobQuestion(newValue);
                        }}
                        options={availableJobQuestions.filter(q => 
                            // Filter out already added IDs from BOTH language arrays
                            !jobQuestionData.en.includes(q.id) && !jobQuestionData.th.includes(q.id)
                        )}
                        getOptionLabel={(option) => option.name}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                label="Select Job Question" 
                                variant="outlined"
                                placeholder="Choose a job question"
                                disabled={isLoading}
                            />
                        )}
                        className="job-question-input"
                        sx={{ flex: 1 }}
                        disabled={isLoading}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAdd}
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        className="add-button"
                        disabled={isLoading || !selectedJobQuestion}
                    >
                        Add ID
                    </Button>
                </div>

                {/* Loading spinner */}
                {isLoading && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <CircularProgress />
                    </div>
                )}

                {/* Job question ID lists - English */}
                {!isLoading && (
                    <>
                        <h4 style={{ marginTop: '30px', marginBottom: '10px', color: '#1976d2' }}>
                            <Chip label="English (EN)" color="primary" size="small" style={{ marginRight: '10px' }} />
                            {jobQuestionData.en.length} IDs
                        </h4>
                        <TableContainer component={Paper} className="job-question-table">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>No.</TableCell>
                                        <TableCell>Job Question ID</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {jobQuestionData.en.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                No job question IDs found for English. Add one above.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        jobQuestionData.en.map((id, index) => (
                                            <TableRow key={`en-${id}`}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>
                                                    <code>{id}</code>
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        onClick={() => handleDelete(id, 'en')}
                                                        color="error"
                                                        size="small"
                                                        disabled={isLoading}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Job question ID lists - Thai */}
                        <h4 style={{ marginTop: '30px', marginBottom: '10px', color: '#9c27b0' }}>
                            <Chip label="Thai (TH)" color="secondary" size="small" style={{ marginRight: '10px' }} />
                            {jobQuestionData.th.length} IDs
                        </h4>
                        <TableContainer component={Paper} className="job-question-table">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>No.</TableCell>
                                        <TableCell>Job Question ID</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {jobQuestionData.th.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                No job question IDs found for Thai. Add one above.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        jobQuestionData.th.map((id, index) => (
                                            <TableRow key={`th-${id}`}>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>
                                                    <code>{id}</code>
                                                </TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        onClick={() => handleDelete(id, 'th')}
                                                        color="error"
                                                        size="small"
                                                        disabled={isLoading}
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
                </div>
            )}

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SettingsComponent;
